import React, { useState } from 'react';
import Type from 'prop-types';
import { autorun } from 'mobx';
import { useToggle, useDebounce } from 'react-use';
import { TextField, CircularProgress, Autocomplete } from '@mui/material';
import { fieldToTextField } from 'formik-mui';
import { useTranslation } from 'hooks';

const AsyncAutocompleteField = ({ textFieldProps, ...props }) => {
  const { delayMs, getOptions, ...other } = props;
  const { error, helperText, ...field } = fieldToTextField(other);
  const { name } = field;
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, toggleOpen] = useToggle(false);
  const { t } = useTranslation();

  const handleInputChange = (event, value) => {
    setSearchQuery(value);
  };

  useDebounce(
    () => {
      autorun(() => {
        if (searchQuery.length !== 0 && isOpen) {
          getOptions(searchQuery);
        }
      });
    },
    delayMs,
    [searchQuery],
  );

  return (
    <Autocomplete
      {...field}
      onOpen={toggleOpen}
      onClose={toggleOpen}
      inputValue={searchQuery}
      loadingText={`${t('loading')}`}
      noOptionsText={t('notFound')}
      getOptionSelected={(option, value) => option.id === value.id}
      onChange={(_, value) => props.form.setFieldValue(name, value)}
      onBlur={() => props.form.setTouched({ [name]: true })}
      onInputChange={handleInputChange}
      includeInputInList
      filterSelectedOptions
      freeSolo
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          name={name}
          helperText={helperText}
          error={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {props.loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      {...other}
    />
  );
};

AsyncAutocompleteField.propTypes = {
  form: Type.shape({
    // eslint-disable-next-line react/forbid-prop-types
    errors: Type.object,
    setTouched: Type.func,
    setFieldValue: Type.func,
  }).isRequired,
  textFieldProps: Type.shape({
    label: Type.string.isRequired,
    required: Type.bool,
    fullWidth: Type.bool,
    margin: Type.oneOf(['none', 'dense', 'normal']),
  }).isRequired,
  getOptions: Type.func.isRequired,
  loading: Type.bool,
  delayMs: Type.number,
};

AsyncAutocompleteField.defaultProps = {
  loading: false,
  delayMs: 1000,
};

export default AsyncAutocompleteField;
